import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../../theme/theme.js';
import ShareIcon from '@mui/icons-material/Share';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

// Hooks
import { useConsentForm } from './hooks/useConsentForm';

// Components
import SignatureDialog from './components/SignatureDialog';

// Screens
import BasicInfoSection from './screens/BasicInfoSection';
import ProcedureDetailsSection from './screens/ProcedureDetailsSection';
import ConsentAcknowledgementSection from './screens/ConsentAcknowledgementSection';
import SignatureSection from './screens/SignatureSection';
import UnableToConsentSection from './screens/UnableToConsentSection';

const ConsentForm = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    formData,
    setFormData,
    errors,
    setErrors,
    loading,
    success,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleNameChange,
    applyTemplate,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
    handleShare
  } = useConsentForm(uhid_no, navigate);

  const [activeStep, setActiveStep] = React.useState(0);
  const steps = ['Demographics', 'Procedure & Risks', 'Signatures'];

  const handleNext = () => {
    const newErrors = { ...errors };
    let isValid = true;
    
    if (activeStep === 0) {
      if (!formData.patient_name || String(formData.patient_name).trim() === '') {
        newErrors.patient_name = 'Patient Name is required';
        isValid = false;
      } else delete newErrors.patient_name;
      
      if (!formData.diagnosis || String(formData.diagnosis).trim() === '') {
        newErrors.diagnosis = 'Diagnosis is required';
        isValid = false;
      } else delete newErrors.diagnosis;
      
      if (!formData.operation_procedure || String(formData.operation_procedure).trim() === '') {
        newErrors.operation_procedure = 'Operation / Procedure is required';
        isValid = false;
      } else delete newErrors.operation_procedure;
    } else if (activeStep === 1) {
      if (!formData.advised_of_major_risk || String(formData.advised_of_major_risk).trim() === '') {
        newErrors.advised_of_major_risk = 'Advised of Major Risk is required';
        isValid = false;
      } else delete newErrors.advised_of_major_risk;
    }
    
    setErrors(newErrors);
    
    if (isValid) {
      setActiveStep((prev) => prev + 1);
    } else {
      const el = document.querySelector('.Mui-error');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  const handleBackStep = () => setActiveStep((prev) => prev - 1);

  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PreOperativePhase', status: 'success', step: 1 },
    });
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  const goToVideoRecorder = () => {
    navigate(`/video?form=${encodeURIComponent("CONSENT FOR MEDICAL / SURGICAL PROCEDURE")}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };

  if (loading && !formData.patient_name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 4, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Button
                onClick={handleBack}
                startIcon={<span>←</span>}
                sx={{ color: '#008080', fontWeight: 600, mb: 1, textTransform: 'none' }}
              >
                Back
              </Button>
              <Typography
                variant="h4"
                sx={{
                  color: '#008080',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 1
                }}
              >
                Medical / Surgical Consent
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.png" alt="Logo" style={{ width: '32px', height: '32px' }} />
              <Typography variant="h6" sx={{ color: '#008080', fontWeight: 600 }}>OTMS</Typography>
            </Box>
          </Box>

          {/* Action Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                    variant="contained"
                    onClick={handleHomeNavigation}
                    sx={{ backgroundColor: '#008080', textTransform: 'none' }}
                >
                Home
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<VideoCallIcon />}
                    onClick={goToVideoRecorder}
                    sx={{ color: '#008080', borderColor: '#008080', textTransform: 'none' }}
                >
                Add Iterative Video
                </Button>
            </Box>
            <IconButton color="primary" onClick={handleShare} sx={{ border: '1px solid #008080' }}>
              <ShareIcon />
            </IconButton>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ 
                    '& .MuiStepLabel-label.Mui-active': { color: '#008080', fontWeight: 700 },
                    '& .MuiStepLabel-label.Mui-completed': { color: '#008080' }
                }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form onSubmit={handleSubmit}>
            {activeStep === 0 && (
              <BasicInfoSection 
                formData={formData} 
                handleChange={handleChange} 
                handleNameChange={handleNameChange}
                setFormData={setFormData}
                applyTemplate={applyTemplate}
                errors={errors} 
              />
            )}

            {activeStep === 1 && (
              <ProcedureDetailsSection 
                formData={formData} 
                handleChange={handleChange} 
                handleNameChange={handleNameChange}
                setFormData={setFormData}
                errors={errors}
              />
            )}

            {activeStep === 2 && (
              <>
                <ConsentAcknowledgementSection 
                  formData={formData}
                  handleChange={handleChange}
                  handleNameChange={handleNameChange}
                />
                <SignatureSection 
                  formData={formData} 
                  handleSignatureOpen={handleSignatureOpen}
                  handleNameChange={handleNameChange}
                  handleChange={handleChange}
                />
                <UnableToConsentSection 
                  formData={formData} 
                  handleChange={handleChange} 
                  handleNameChange={handleNameChange}
                  handleSignatureOpen={handleSignatureOpen}
                  setFormData={setFormData}
                />
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6, gap: 2 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBackStep}
                variant="outlined"
                sx={{ color: '#008080', borderColor: '#008080' }}
              >
                Back
              </Button>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DraftSaveButton
                  formKey={DRAFT_KEYS.CONSENT_FOR_MEDICAL}
                  uhidNo={uhid_no}
                  formData={formData}
                  onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                  variant="outlined"
                  size="medium"
                  isUpdate={success}
                />
                {activeStep === steps.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      backgroundColor: '#008080',
                      '&:hover': { backgroundColor: '#006666' },
                      px: 4,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: 'none'
                    }}
                  >
                    {loading ? "Processing..." : (success ? "Update Consent" : "Submit Consent")}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ backgroundColor: '#008080', textTransform: 'none' }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </form>

          {/* Dialogs */}
          <SignatureDialog
            open={signatureDialogOpen}
            onClose={() => setSignatureDialogOpen(false)}
            onSave={handleSignatureSave}
            signaturePadRef={signaturePadRef}
          />
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default ConsentForm;
